import { AccessibleForm, Alert, Col, Loader, Row, TextInput, themes } from '@digitools/honest-react';
import { Heading } from '@digitools/honest-react/dist/lib/components/Heading';
import queryString from 'query-string';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import { withRouter } from 'src/utils/withRouter';
import styled, { css } from 'styled-components';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import useAuthentication from '../../../../hooks/useAuthentication';
import useExperience from '../../../../hooks/useExperience';
import usePortalMessenger from '../../../../hooks/usePortalMessenger';
import usePortalStatus from '../../../../hooks/usePortalStatus';
import useTealium from '../../../../hooks/useTealium';
import useTranslate from '../../../../hooks/useTranslate';
import { EventAction, GoogleAnalyticsContext, } from 'src/providers/GoogleAnalyticsProvider';
import { UnexpectedErrorContext } from 'src/providers/UnexpectedErrorProvider';
import { gaLabelFactory } from 'src/utils/gaUtils';
import getDisplayText from '../../../../utils/languageDisplay';
import * as actions from '../actions';
import { LoginDisplayTextEn } from '../i18n/LoginDisplayTextEn';
import { LoginDisplayTextEs } from '../i18n/LoginDisplayTextEs';
import reducer, { initialState } from '../reducer';
import { getEnvVariable } from 'src/utils/envUtils';
import { EEMigrationLoginText } from './../i18n/EEMigrationLoginText';
import { StyledSpoeBox, StyledSpoeButton } from '../../vanityPage/components/StyledComponents';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { MOBILE } from 'src/constants/ScreenSizeConstants';
const GA_FEATURE_PREFIX = 'LOGIN';
const generateGALabel = gaLabelFactory(GA_FEATURE_PREFIX);
const disabledOverlayRules = css `
  opacity: 0.4;
  pointer-events: none;
`;
const DisabledOverlay = styled.div `
  ${({ maintenanceModeEnabled }) => (maintenanceModeEnabled ? disabledOverlayRules : undefined)};
`;
const lfgHomeLogin = `${getEnvVariable('lfgLoginUrl')}`;
const LoginBox = ({ location, }) => {
    const { trackError } = useContext(GoogleAnalyticsContext);
    const { login, logout, user } = useAuthentication();
    const { language } = useExperience();
    const { setIsMessagingDisabled } = useContext(UnexpectedErrorContext);
    const { isStatusLoading, maintenanceModeEnabled, maintenanceModeBackdoorAccessEnabled } = usePortalStatus();
    const { addMessage, clearMessages } = usePortalMessenger();
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslate();
    const { trackEvent } = useTealium();
    const isMobile = useMediaQuery(`(max-width: ${MOBILE})`);
    const isBackDoor = location.pathname === '/backdoor/login';
    const getLocalDisplayText = (key) => getDisplayText.apply({}, [key, LoginDisplayTextEn, LoginDisplayTextEs, language]);
    const [loginAlert, dispatch] = useReducer(reducer, initialState);
    useEffect(() => {
        if (user) {
            logout();
        }
        if (sessionStorage.getItem('isLfgUser') === 'true')
            window.location.href = getEnvVariable('lfgHomeUrl') + `/secure/logout`;
        setIsMessagingDisabled(false);
        const values = queryString.parse(location.search);
        if (values.success === 'true' &&
            values.message === 'Your email was verified. You can continue using the application.') {
            dispatch(actions.emailVerified());
        }
        if (values.success === 'true' && values.message === 'Your account has been unblocked.') {
            dispatch(actions.accountUnblocked());
        }
        if (values.success === 'false' && values.message === 'Access expired.' && values.verificationEmail === 'true') {
            dispatch(actions.emailVerificationExpired());
        }
        if (values.success === 'false' && values.message === 'Access expired.' && values.changePassword === 'true') {
            dispatch(actions.passwordEmailExpired());
        }
        return () => {
            clearMessages();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const clearError = () => {
        dispatch(actions.clear());
    };
    const handleLoginError = (err) => {
        sessionStorage.removeItem('loginClicked');
        if (err) {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_name: err.code || 'generic error',
                event_type: TEALIUM_EVENT.EVENT_TYPE.LOGIN,
                event_version: TEALIUM_EVENT.EVENT_VERSION.ACCOUNT_LOOKUP,
            });
        }
        if (err && err.code === 'access_denied') {
            trackError(EventAction.CLICK, generateGALabel('auth0-login-access_denied'));
            dispatch(actions.accessDeniedSpoe());
        }
        else if (err && err.code === 'too_many_attempts') {
            trackError(EventAction.CLICK, generateGALabel('auth0-login-too_many_attempts'));
            dispatch(actions.tooManyAttemptsSpoe());
        }
        else {
            trackError(EventAction.CLICK, generateGALabel('auth0-login-unexpected'));
            dispatch(actions.unexpected());
        }
        setIsLoading(false);
    };
    const handleSubmit = async ({ username, password }) => {
        dispatch(actions.clear());
        setIsLoading(true);
        try {
            login(username, password, handleLoginError);
        }
        catch (e) {
            const { response: { status = 500 } = { status: 500 } } = e;
            if (status === 400 || status === 401) {
                dispatch(actions.userProfileNotFound());
            }
            else {
                trackError(EventAction.CLICK, generateGALabel('login-generic-error'));
            }
            setIsLoading(false);
        }
    };
    const trackForgotUsername = () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_name: TEALIUM_EVENT.EVENT_NAME.FORGOT_PASSWORD,
            event_type: TEALIUM_EVENT.EVENT_TYPE.LOGIN,
            event_version: TEALIUM_EVENT.EVENT_VERSION.ACCOUNT_LOOKUP,
        });
    };
    const trackRegisterUser = () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_name: TEALIUM_EVENT.EVENT_NAME.REGISTER,
            event_type: TEALIUM_EVENT.EVENT_TYPE.LOGIN,
            event_version: TEALIUM_EVENT.EVENT_VERSION.ACCOUNT_LOOKUP,
        });
    };
    const format = (value) => (value != null ? value.trim() : value);
    const parse = (value) => (value != null ? value.trim() : value);
    const spoeRegisterNowText = t(EEMigrationLoginText.registerNow).replace('.', '');
    const RegisterAccountEmailVerify = () => {
        return (<>
        <Row className={`text-center ${!isMobile ? 'pb-2' : ''}`}>
          <Col>
            <Link to={'/public/registration'} onClick={trackRegisterUser} data-testid={'register-link'}>
              {spoeRegisterNowText}
            </Link>
          </Col>
        </Row>
      </>);
    };
    //TODO: collapse into main jsx - no need for sub-comp
    const EEMigrationLoginHeading = () => {
        return (<>
        <Row>
          <Col xl={12} className={`${loginAlert ? 'pb-2 mb-0' : 'pb-4 mb-2'} px-0 text-center`}>
            <Heading elemType={'h1'} elemStyle={'h3'} data-public={true} className={'mt-3'} color={'primaryDarker'} theme={themes.lincoln} style={{ fontSize: '30px' }}>
              {getLocalDisplayText('employersOrBrokers')}
            </Heading>
          </Col>
        </Row>
      </>);
    };
    return (<StyledSpoeBox id='LoginBox'>
    <Loader id='login-spinner' isShown={isLoading || isStatusLoading} data-testid={'loading-indicator'}>
      {(maintenanceModeEnabled || maintenanceModeBackdoorAccessEnabled) && !isBackDoor ? (<div data-testid={'maintenance-overlay'}>
          <EEMigrationLoginHeading />
          <Alert data-public={true} type={'warning'}>
            <strong>{getLocalDisplayText('portalDownTitle')}</strong>{' '}
            {getLocalDisplayText('portalDownBody')}
          </Alert>
        </div>) : (<DisabledOverlay maintenanceModeEnabled={(maintenanceModeEnabled || maintenanceModeBackdoorAccessEnabled) && !isBackDoor} data-testid={'maintenance-overlay'}>
        <EEMigrationLoginHeading />
        {!!loginAlert && (<Alert data-public={true} type={loginAlert.type} data-testid={'alert-message'}>
            <div style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
              <strong>{getLocalDisplayText(loginAlert.alertTitle)}</strong> {getLocalDisplayText(loginAlert.alertBody)}
            </div>
            <br />
            <div style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
              {getLocalDisplayText('additionalLoginFailedInfoSpoe')}{' '}
              <a href={getEnvVariable('lfgHomeUrl')}>LincolnFinancial.com.</a>
            </div>
          </Alert>)}

        {location?.state?.state?.error?.message && (<>
            {location?.state?.state?.error?.message.startsWith('Unable to configure verification page') ? (<Alert data-public={true} type={'warning'} data-testid={'tpcookies-message'}>
                <div>
                  <strong>{getLocalDisplayText('notice')}</strong>
                  {getLocalDisplayText('thirdPartyCookies')}
                </div>
              </Alert>) : (<Alert data-public={true} type={'error'} data-testid={'auth0-alert'}>
                <div>
                  <strong>{getLocalDisplayText('unexpectedTitle')}</strong>
                  {getLocalDisplayText('auth0error')}
                </div>
              </Alert>)}
          </>)}
        <AccessibleForm onSubmit={handleSubmit}>
          <TextInput data-public={true} data-testid={'username-field'} field={'username'} format={format} label={getLocalDisplayText('username')} placeholder={getLocalDisplayText('usernamePlaceholder')} onChange={clearError} parse={parse} required={true} validateOnBlur={true}/>

          <TextInput data-private={true} data-testid={'password-field'} field={'password'} label={getLocalDisplayText('password')} placeholder={getLocalDisplayText('passwordPlaceholder')} onChange={clearError} type={'password'} required={true} validateOnBlur={true} toggleShowPassword={true}/>
          <StyledSpoeButton data-public={true} buttonType={'primary'} type={'submit'} style={{ width: '100%' }} onClick={() => sessionStorage.setItem('loginClicked', 'true')} data-testid={'submit-button'}>
            {getLocalDisplayText('login')}
          </StyledSpoeButton>
        </AccessibleForm>
        <Row>
          <Col className={`mt-3 w-100 pl-0 pr-0 pt-4`}>
            <RegisterAccountEmailVerify />
          </Col>
        </Row>
        <Row>
          <Col data-public={true} className={'text-center w-100 pt-0'}>
            <Link data-public={true} to={{ pathname: '/public/forgot-info', search: '?employer=true' }} onClick={trackForgotUsername} data-testid={'forgot-username-link'}>
              {getLocalDisplayText('forgotUserNamePassword')}
            </Link>
          </Col>
        </Row>
      </DisabledOverlay>)}
    </Loader>
    </StyledSpoeBox>);
};
export default withRouter(LoginBox);
